import React, { useContext, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  ImportPriceJsonRootEntry,
  ImportPriceJsonToDbRequest,
  ImportSchenkerCountryConfigurationRequestInDTO,
  PermissionTypesEnum,
} from '@api/ringil-api.v2.tsx';
import SvgId from '@icons/id.svg?react';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { ApiContext } from '@api/api.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import SvgImport from '@icons/file-inverted.svg?react';
import { useCreateInputPasteTabular } from '@components/framework/input/InputPasteTabular/InputPasteTabular.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgSuccess from '@icons/check-circle.svg?react';
import SvgAlert from '@icons/alert.svg?react';

interface ComponentProps {
  companyId: string;
}

interface ImportSchenkerCountryItem {
  countryIso3: string;
  weightCBMCoeff: number;
  tollFee: number;
  fuelSurchargePercentage: number;
  fixDayPrice: number;
  fixDayPrice10?: number;
  fixDayPrice13?: number;
  longGoodPrice?: number;
  fixDayToAgreedPrice?: number;
  automatedFixDayToAgreed?: number;
  premium10?: number;
  premium13?: number;
  currency: string;
  ecoFee?: number;
}

interface ImportCountryConfigurationSummary {
  created: number;
  updated: number;
  total: number;
  validationMessage?: string;
}

interface ImportPriceJson {
  weightBandsCount: number;
  priceMatrixCount: number;
  skippedCount: number;
  message: string;
  validationMessage?: string;
  parseError?: string;
}


const CompanyProfileSchenkerCatalogs: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const {
    importCountryConfigurations,
    importPriceFromJson,
  } = useContext(ApiContext);

  const catalogsFlag = useSecuredFlag('manage-schenker-catalogs', {
    kind: 'visibility',
    description: 'Manage Schenker catalogs',
    permissions: [PermissionTypesEnum.PERMISSION_ADMIN_CODELISTS],
  });

  const [importCountryConfigurationSummary, setImportCountryConfigurationSummary] = useState<ImportCountryConfigurationSummary>();
  const [importCZPriceJsonSummary, setImportCZPriceJsonSummary] = useState<ImportPriceJson>();
  const [importOtherExportPriceJsonSummary, setImportOtherExportPriceJsonSummary] = useState<ImportPriceJson>();
  const [importOtherImportPriceJsonSummary, setImportOtherImportPriceJsonSummary] = useState<ImportPriceJson>();

  const fileInputCZRef = useRef<HTMLInputElement>(null);
  const fileInputOtherExportRef = useRef<HTMLInputElement>(null);
  const fileInputOtherImportRef = useRef<HTMLInputElement>(null);

  const {
    InputPasteTabular,
    setVisible: setVisibleImportTable,
  } = useCreateInputPasteTabular<ImportSchenkerCountryItem>(['countryIso3', 'weightCBMCoeff', 'tollFee', 'fuelSurchargePercentage', 'fixDayPrice', 'fixDayPrice10', 'fixDayPrice13', 'longGoodPrice', 'fixDayToAgreedPrice', 'automatedFixDayToAgreed', 'premium10', 'premium13', 'currency', 'ecoFee']);

  const handleImportSave = async (rows: ImportSchenkerCountryItem[]) => {
    const grouped = rows.reduce<Record<string, ImportSchenkerCountryItem[]>>((acc, row) => {
      if (!acc[row.currency]) acc[row.currency] = [];
      acc[row.currency].push(row);
      return acc;
    }, {});

    setImportCountryConfigurationSummary({
      total: 0,
      created: 0,
      updated: 0,
    });

    for (const [currency, items] of Object.entries(grouped)) {
      const request: ImportSchenkerCountryConfigurationRequestInDTO = {
        configs: items,
        currency: currency,
      };
      await new Promise<void>((resolve) => {
        importCountryConfigurations(
          request,
          data => {
            if (data) {
              setImportCountryConfigurationSummary(prev => ({
                total: (prev?.total || 0) + (data.total || 0),
                created: (prev?.created || 0) + (data.created || 0),
                updated: (prev?.updated || 0) + (data.updated || 0),
              }));
            }
            resolve();
          },
          null,
          {
            onValidationFailed: (originalResponse: any) => {
              setImportCountryConfigurationSummary(prev => ({
                ...prev,
                validationMessage: originalResponse[0]?.message || tr(`CompanyProfileSchenkerCatalog.validationFailed`, `Validation failed`),
              }));
            },
          },
        );
      });
    }
  };

  const handleCZFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file && file.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsedData = JSON.parse(e.target?.result as string) as ImportPriceJsonRootEntry[];
          const request: ImportPriceJsonToDbRequest = {
            jsonData: parsedData,
            isExport: true,
          };

          importPriceFromJson(
            request,
            (result) => {
              setImportCZPriceJsonSummary(result);
              if (fileInputCZRef.current) {
                fileInputCZRef.current.value = '';
              }
            },
            null,
            {
              onValidationFailed: (originalResponse: any) => {
                setImportCZPriceJsonSummary(prev => ({
                  ...prev,
                  validationMessage: originalResponse[0]?.message || tr(`CompanyProfileSchenkerCatalog.validationFailed`, `Validation failed`),
                }));
                if (fileInputCZRef.current) {
                  fileInputCZRef.current.value = '';
                }
              },
            },
          );
        } catch (error) {
          setImportCZPriceJsonSummary(prev => ({
            ...prev,
            parseError: tr(`CompanyProfileSchenkerCatalog.parseError`, `File parsing failed`),
          }));
          if (fileInputCZRef.current) {
            fileInputCZRef.current.value = '';
          }
        }
      };
      reader.readAsText(file);
    } else {
      setImportCZPriceJsonSummary(prev => ({
        ...prev,
        parseError: tr(`CompanyProfileSchenkerCatalog.wrongFileTypeError`, `Wrong file type provided`),
      }));
      if (fileInputCZRef.current) {
        fileInputCZRef.current.value = '';
      }
    }
  };

  const handleOtherExportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file && file.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsedData = JSON.parse(e.target?.result as string) as ImportPriceJsonRootEntry[];
          const request: ImportPriceJsonToDbRequest = {
            jsonData: parsedData,
            isExport: true,
          };

          importPriceFromJson(
            request,
            (result) => {
              setImportOtherExportPriceJsonSummary(result);
              if (fileInputOtherExportRef.current) {
                fileInputOtherExportRef.current.value = '';
              }
            },
            null,
            {
              onValidationFailed: (originalResponse: any) => {
                setImportOtherExportPriceJsonSummary(prev => ({
                  ...prev,
                  validationMessage: originalResponse[0]?.message || tr(`CompanyProfileSchenkerCatalog.validationFailed`, `Validation failed`),
                }));
                if (fileInputOtherExportRef.current) {
                  fileInputOtherExportRef.current.value = '';
                }
              },
            },
          );
        } catch (error) {
          setImportOtherExportPriceJsonSummary(prev => ({
            ...prev,
            parseError: tr(`CompanyProfileSchenkerCatalog.parseError`, `File parsing failed`),
          }));
          if (fileInputOtherExportRef.current) {
            fileInputOtherExportRef.current.value = '';
          }
        }
      };
      reader.readAsText(file);
    } else {
      setImportOtherExportPriceJsonSummary(prev => ({
        ...prev,
        parseError: tr(`CompanyProfileSchenkerCatalog.wrongFileTypeError`, `Wrong file type provided`),
      }));
      if (fileInputOtherExportRef.current) {
        fileInputOtherExportRef.current.value = '';
      }
    }
  };

  const handleOtherImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file && file.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsedData = JSON.parse(e.target?.result as string) as ImportPriceJsonRootEntry[];
          const request: ImportPriceJsonToDbRequest = {
            jsonData: parsedData,
            isExport: false,
          };

          importPriceFromJson(
            request,
            (result) => {
              setImportOtherImportPriceJsonSummary(result);
              if (fileInputOtherImportRef.current) {
                fileInputOtherImportRef.current.value = '';
              }
            },
            null,
            {
              onValidationFailed: (originalResponse: any) => {
                setImportOtherImportPriceJsonSummary(prev => ({
                  ...prev,
                  validationMessage: originalResponse[0]?.message || tr(`CompanyProfileSchenkerCatalog.validationFailed`, `Validation failed`),
                }));
                if (fileInputOtherImportRef.current) {
                  fileInputOtherImportRef.current.value = '';
                }
              },
            },
          );
        } catch (error) {
          setImportOtherImportPriceJsonSummary(prev => ({
            ...prev,
            parseError: tr(`CompanyProfileSchenkerCatalog.parseError`, `File parsing failed`),
          }));
          if (fileInputOtherImportRef.current) {
            fileInputOtherImportRef.current.value = '';
          }
        }
      };
      reader.readAsText(file);
    } else {
      setImportOtherImportPriceJsonSummary(prev => ({
        ...prev,
        parseError: tr(`CompanyProfileSchenkerCatalog.wrongFileTypeError`, `Wrong file type provided`),
      }));
      if (fileInputOtherImportRef.current) {
        fileInputOtherImportRef.current.value = '';
      }
    }
  };

  return (
    <>
      <SecuredArea id="schenker-catalogs-section" label="Schenker catalogs section" description="The company's Schenker code lists" visible={catalogsFlag}>
        <>
          <div
            className="flex justify-between items-center self-stretch p-4 px-8 rounded-lg bg-white shadow-[0_2px_8px_0_rgba(0,0,0,0.05),0_16px_48px_0_rgba(254,80,0,0.02)] cursor-pointer"
            onClick={() => setVisibleImportTable(true)}
          >
            <div className="flex flex-row gap-4 items-center">
              <COHeadline
                variant={'h3'}
                title={tr('CompanyProfileSchenkerCatalog.countryCatalogTitle', 'Country settings')}
                icon={<SvgId className={'w-4 h-4 text-content-tertiary'} />}
              />
              <a
                href={'/imports/importSchenkerCountriesSettings.xlsx'}>{tr(`CompanyProfileSchenkerCatalog.importTemplate`, `Download template`)}</a>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <SecuredArea
                id="schenker-country-import-success"
                label="Country import success message"
                visible={[{ type: 'data', label: 'country import created/updated > 0', satisfied: !!(importCountryConfigurationSummary && (importCountryConfigurationSummary.created > 0 || importCountryConfigurationSummary.updated > 0)) }]}
              >
                <COInlineMessage
                  variant={'success'}
                  icon={<span className={'text-content-tertiary'}><SvgSuccess /></span>}
                >
                 <span className={'text-content-primary'}>
                   {tr('CompanyProfileCatalog.importSuccessMessage', 'Import was successful. We have created {{createCount}} entries. We have updated {{updatedCount}} entries', {
                     createCount: importCountryConfigurationSummary?.created,
                     updatedCount: importCountryConfigurationSummary?.updated,
                   })}
                 </span>
                </COInlineMessage>
              </SecuredArea>
              <SecuredArea
                id="schenker-country-import-error"
                label="Country import error message"
                visible={[{ type: 'data', label: 'country import has validationMessage', satisfied: !!(importCountryConfigurationSummary && importCountryConfigurationSummary.validationMessage) }]}
              >
                <COInlineMessage
                  variant={'error'}
                  icon={<span className={'text-content-tertiary'}><SvgAlert /></span>}
                >
                 <span className={'text-content-primary'}>
                   {importCountryConfigurationSummary?.validationMessage}
                 </span>
                </COInlineMessage>
              </SecuredArea>
              <RingilButton
                label={tr(`CompanyProfileSchenkerCatalog.newImport`, `New import`)}
                mode={'table'}
                icon={<SvgImport />}
                onClick={() => {
                }}
              />
            </div>
          </div>

          <div
            className="flex justify-between items-center self-stretch p-4 px-8 rounded-lg bg-white shadow-[0_2px_8px_0_rgba(0,0,0,0.05),0_16px_48px_0_rgba(254,80,0,0.02)] cursor-pointer"
            onClick={() => fileInputCZRef.current?.click()}
          >
            <div className="flex flex-row gap-4 items-center">
              <COHeadline
                variant={'h3'}
                title={tr('CompanyProfileSchenkerCatalog.czPriceTitle', 'CZ Price settings')}
                icon={<SvgId className={'w-4 h-4 text-content-tertiary'} />}
              />
            </div>
            <div className="flex flex-row gap-4 items-center">
              <SecuredArea
                id="schenker-cz-import-success"
                label="CZ price import success message"
                visible={[{ type: 'data', label: 'CZ import weightBandsCount truthy', satisfied: !!(importCZPriceJsonSummary && importCZPriceJsonSummary.weightBandsCount) }]}
              >
                <COInlineMessage
                  variant={'success'}
                  icon={<span className={'text-content-tertiary'}><SvgSuccess /></span>}
                >
                 <span className={'text-content-primary'}>
                  {tr('CompanyProfileCatalog.czPricesImportSuccess', 'Import was successful. Created {{weightBandsCount}} weight bands and {{priceMatrixCount}} price matrices. Skipped {{skippedCount}} entries.', {
                     weightBandsCount: importCZPriceJsonSummary?.weightBandsCount,
                     priceMatrixCount: importCZPriceJsonSummary?.priceMatrixCount,
                     skippedCount: importCZPriceJsonSummary?.skippedCount,
                   })}
                 </span>
                </COInlineMessage>
              </SecuredArea>
              <SecuredArea
                id="schenker-cz-import-error"
                label="CZ price import error message"
                visible={[{ type: 'data', label: 'CZ import has validationMessage or parseError', satisfied: !!(importCZPriceJsonSummary && (importCZPriceJsonSummary.validationMessage || importCZPriceJsonSummary.parseError)) }]}
              >
                <COInlineMessage
                  variant={'error'}
                  icon={<span className={'text-content-tertiary'}><SvgAlert /></span>}
                >
                 <span className={'text-content-primary'}>
                   {importCZPriceJsonSummary?.validationMessage}
                   {importCZPriceJsonSummary?.parseError}
                 </span>
                </COInlineMessage>
              </SecuredArea>
              <RingilButton
                label={tr(`CompanyProfileSchenkerCatalog.czPriceImport`, `Import CZ prices from json`)}
                mode={'table'}
                icon={<SvgImport />}
                onClick={() => {}}
              />
            </div>
          </div>

          <div
            className="flex justify-between items-center self-stretch p-4 px-8 rounded-lg bg-white shadow-[0_2px_8px_0_rgba(0,0,0,0.05),0_16px_48px_0_rgba(254,80,0,0.02)] cursor-pointer"
            onClick={() => fileInputOtherExportRef.current?.click()}
          >
            <div className="flex flex-row gap-4 items-center">
              <COHeadline
                variant={'h3'}
                title={tr('CompanyProfileSchenkerCatalog.otherExportPriceTitle', 'Other Export Price settings')}
                icon={<SvgId className={'w-4 h-4 text-content-tertiary'} />}
              />
            </div>
            <div className="flex flex-row gap-4 items-center">
              <SecuredArea
                id="schenker-other-export-import-success"
                label="Other Export price import success message"
                visible={[{ type: 'data', label: 'Other Export import weightBandsCount truthy', satisfied: !!(importOtherExportPriceJsonSummary && importOtherExportPriceJsonSummary.weightBandsCount) }]}
              >
                <COInlineMessage
                  variant={'success'}
                  icon={<span className={'text-content-tertiary'}><SvgSuccess /></span>}
                >
                 <span className={'text-content-primary'}>
                  {tr('CompanyProfileCatalog.otherExportPricesImportSuccess', 'Import was successful. Created {{weightBandsCount}} weight bands and {{priceMatrixCount}} price matrices. Skipped {{skippedCount}} entries.', {
                    weightBandsCount: importOtherExportPriceJsonSummary?.weightBandsCount,
                    priceMatrixCount: importOtherExportPriceJsonSummary?.priceMatrixCount,
                    skippedCount: importOtherExportPriceJsonSummary?.skippedCount,
                  })}
                 </span>
                </COInlineMessage>
              </SecuredArea>
              <SecuredArea
                id="schenker-other-export-import-error"
                label="Other Export price import error message"
                visible={[{ type: 'data', label: 'Other Export import has validationMessage or parseError', satisfied: !!(importOtherExportPriceJsonSummary && (importOtherExportPriceJsonSummary.validationMessage || importOtherExportPriceJsonSummary.parseError)) }]}
              >
                <COInlineMessage
                  variant={'error'}
                  icon={<span className={'text-content-tertiary'}><SvgAlert /></span>}
                >
                 <span className={'text-content-primary'}>
                   {importOtherExportPriceJsonSummary?.validationMessage}
                   {importOtherExportPriceJsonSummary?.parseError}
                 </span>
                </COInlineMessage>
              </SecuredArea>
              <RingilButton
                label={tr(`CompanyProfileSchenkerCatalog.otherExportPriceImport`, `Import Other Export prices from json`)}
                mode={'table'}
                icon={<SvgImport />}
                onClick={() => {}}
              />
            </div>
          </div>

          <div
            className="flex justify-between items-center self-stretch p-4 px-8 rounded-lg bg-white shadow-[0_2px_8px_0_rgba(0,0,0,0.05),0_16px_48px_0_rgba(254,80,0,0.02)] cursor-pointer"
            onClick={() => fileInputOtherImportRef.current?.click()}
          >
            <div className="flex flex-row gap-4 items-center">
              <COHeadline
                variant={'h3'}
                title={tr('CompanyProfileSchenkerCatalog.otherImportPriceTitle', 'Other Import Price settings')}
                icon={<SvgId className={'w-4 h-4 text-content-tertiary'} />}
              />
            </div>
            <div className="flex flex-row gap-4 items-center">
              <SecuredArea
                id="schenker-other-import-import-success"
                label="Other Import price import success message"
                visible={[{ type: 'data', label: 'Other Import import weightBandsCount truthy', satisfied: !!(importOtherImportPriceJsonSummary && importOtherImportPriceJsonSummary.weightBandsCount) }]}
              >
                <COInlineMessage
                  variant={'success'}
                  icon={<span className={'text-content-tertiary'}><SvgSuccess /></span>}
                >
                 <span className={'text-content-primary'}>
                  {tr('CompanyProfileCatalog.otherImportPricesImportSuccess', 'Import was successful. Created {{weightBandsCount}} weight bands and {{priceMatrixCount}} price matrices. Skipped {{skippedCount}} entries.', {
                    weightBandsCount: importOtherImportPriceJsonSummary?.weightBandsCount,
                    priceMatrixCount: importOtherImportPriceJsonSummary?.priceMatrixCount,
                    skippedCount: importOtherImportPriceJsonSummary?.skippedCount,
                  })}
                 </span>
                </COInlineMessage>
              </SecuredArea>
              <SecuredArea
                id="schenker-other-import-import-error"
                label="Other Import price import error message"
                visible={[{ type: 'data', label: 'Other Import import has validationMessage or parseError', satisfied: !!(importOtherImportPriceJsonSummary && (importOtherImportPriceJsonSummary.validationMessage || importOtherImportPriceJsonSummary.parseError)) }]}
              >
                <COInlineMessage
                  variant={'error'}
                  icon={<span className={'text-content-tertiary'}><SvgAlert /></span>}
                >
                 <span className={'text-content-primary'}>
                   {importOtherImportPriceJsonSummary?.validationMessage}
                   {importOtherImportPriceJsonSummary?.parseError}
                 </span>
                </COInlineMessage>
              </SecuredArea>
              <RingilButton
                label={tr(`CompanyProfileSchenkerCatalog.otherImportPriceImport`, `Import Other Import prices from json`)}
                mode={'table'}
                icon={<SvgImport />}
                onClick={() => {}}
              />
            </div>
          </div>
        </>
      </SecuredArea>


      <InputPasteTabular onSave={rows => {
        handleImportSave(rows);
      }} />

      <input
        type="file"
        ref={fileInputCZRef}
        style={{ display: 'none' }}
        accept=".json"
        onChange={handleCZFileChange}
      />
      <input
        type="file"
        ref={fileInputOtherExportRef}
        style={{ display: 'none' }}
        accept=".json"
        onChange={handleOtherExportFileChange}
      />
      <input
        type="file"
        ref={fileInputOtherImportRef}
        style={{ display: 'none' }}
        accept=".json"
        onChange={handleOtherImportFileChange}
      />
    </>
  );
};


export default CompanyProfileSchenkerCatalogs;
